import React, { useEffect, useRef, useState } from 'react';
import { Alert, Collapse, Table, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import { useAccess } from '../../../../auth/AccessContext.jsx';
import StatusTag from '../../../../components/StatusTag.jsx';
import { STATUS_STATE, dayLabel, historyRows, statusBanner } from './statementStatus.js';

const { Text } = Typography;

const dash = (value) => (value === null || value === undefined || value === '' ? <Text type="secondary">—</Text> : value);

/** The Statement history's columns (design 1.5): statementStatus.js historyRows gives each cell its words. */
const COLUMNS = [
    {
        title: 'Statement date',
        key: 'statementDate',
        width: 130,
        render: (_, row) => <>{row.dateLabel}{!row.expected && <Text type="secondary"> · not a business day</Text>}</>
    },
    { title: 'Account', dataIndex: 'accountLabel', key: 'account' },
    { title: 'Status', key: 'status', render: (_, row) => <StatusTag color={row.statusColor} label={row.statusLabel} help={row.statusTitle} /> },
    { title: 'Lines', dataIndex: 'lines', key: 'lines', render: dash },
    { title: 'Credits (LKR)', dataIndex: 'credits', key: 'credits', align: 'right', render: dash },
    { title: 'Debits (LKR)', dataIndex: 'debits', key: 'debits', align: 'right', render: dash },
    { title: 'Reconciled', dataIndex: 'reconciled', key: 'reconciled', render: dash },
    { title: 'Outstanding', dataIndex: 'outstanding', key: 'outstanding', align: 'right', render: dash },
    { title: 'Uploaded', dataIndex: 'uploaded', key: 'uploaded', render: dash }
];

/**
 * The panel as it is drawn, apart from its read so a render test can show it
 * (test/ssr.js runs no effect): the banner, and the collapsed Statement
 * history under it - or, when the status could not be read, a small error
 * that says the lines' completeness is not known. Nothing before an answer.
 *
 * @param {{ status: object|null, error?: string|null }} props
 */
export const StatementStatusView = ({ status, error = null }) => {
    const banner = statusBanner(status);
    const rows = historyRows(status);
    if (!banner && !error) return null;
    return (
        <div className="statement-status">
            {error && (
                <Alert type="error" showIcon className="statement-status-alert"
                    title={'The statement status could not be read (' + error + '): whether the lines below cover every expected statement is not known.'} />
            )}
            {banner && (
                <Alert
                    type={banner.type}
                    showIcon
                    className="statement-status-alert"
                    icon={status.state === STATUS_STATE.PROCESSING ? <LoadingOutlined /> : undefined}
                    title={banner.title}
                    description={banner.lines.length ? (
                        <ul className="statement-status-lines">
                            {banner.lines.map((line, i) => <li key={i}>{line}</li>)}
                        </ul>
                    ) : undefined}
                />
            )}
            {rows.length > 0 && (
                <Collapse
                    ghost
                    size="small"
                    className="statement-history"
                    items={[{
                        key: 'history',
                        label: (
                            <span>
                                Statement history
                                {status.windowFrom && <Text type="secondary">{' · from ' + dayLabel(status.windowFrom)}</Text>}
                            </span>
                        ),
                        children: (
                            <Table
                                className="statement-history-table"
                                rowKey="key"
                                size="small"
                                columns={COLUMNS}
                                dataSource={rows}
                                pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false, size: 'small' }}
                                scroll={{ x: 'max-content' }}
                            />
                        )
                    }]}
                />
            )}
        </div>
    );
};

/**
 * LWM1-479 (IP-175; design 2026-10-13 section 1.5): above the Bank Statement
 * lines, which statements they come from and whether reconciliation is
 * still processing, waiting for statements or complete (GET
 * cash/statement-status) - for the screen's fund filter, or every fund. It
 * reads on mount and whenever fundId or refreshKey changes; the screen bumps
 * refreshKey each time it reads its lines again (Refresh, the live topics,
 * after an action), so the two never disagree for long. A later read wins,
 * another fund's answer is never shown as this one's, and a failed read is
 * said here without holding up the lines. onStatus hears every answer (null
 * while unknown or unreadable), so the screen can mark its counts incomplete.
 *
 * @param {{ fundId?: string|null, refreshKey?: number, onStatus?: (status: object|null) => void }} props
 */
const StatementStatusPanel = ({ fundId = null, refreshKey = 0, onStatus }) => {
    const { api } = useAccess();
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    // A later read wins: an answer to an earlier fund or refresh is dropped.
    const latest = useRef(0);
    // The fund whose status is on screen; undefined before the first read.
    const shownFor = useRef(undefined);
    const report = useRef(onStatus);
    report.current = onStatus;

    useEffect(() => {
        const ticket = ++latest.current;
        if (shownFor.current !== fundId) {
            // Another fund: the last one's position is cleared rather than shown as this one's.
            shownFor.current = fundId;
            setStatus(null);
            setError(null);
            report.current?.(null);
        }
        api.cash.statementStatus({ fundId }).then((answer) => {
            if (ticket !== latest.current) return;
            setStatus(answer ?? null);
            setError(null);
            report.current?.(answer ?? null);
        }, (e) => {
            if (ticket !== latest.current) return;
            // A stale banner could say "complete" of lines that no longer are: the error stands alone.
            setStatus(null);
            setError(e.message);
            report.current?.(null);
        });
        return () => {
            latest.current += 1;
        };
    }, [api, fundId, refreshKey]);

    return <StatementStatusView status={status} error={error} />;
};

export default StatementStatusPanel;
